import React, { useEffect, useRef } from 'react';

/**
 * FooterBackground — neural-network animation tuned for the Landing footer band.
 *
 * Design choices that differentiate it from <HeroBackground>:
 *  - Lower particle density (footer is shorter, you want it calm)
 *  - Cooler palette (deep teal + indigo + slate-400 dots) so it feels like the
 *    "bottom of the system" — gravitas — vs. the bright neon excitement of the hero.
 *  - Vertical mesh gradient that fades into the page above (continuous canvas feel).
 *  - Honors prefers-reduced-motion.
 */
const FooterBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    // Footer should feel calmer — fewer nodes, a touch slower, but still clearly visible.
    const PARTICLE_COUNT = window.innerWidth < 768 ? 38 : 78;
    const COLORS = [
      'rgba(20,184,166,',  // teal
      'rgba(56,189,248,',  // sky
      'rgba(99,102,241,',  // indigo (richer for footer)
      'rgba(34,211,238,',  // light cyan
      'rgba(16,185,129,',  // emerald (carries hero palette down)
    ];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        radius: Math.random() * 1.4 + 0.7,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        opacity: Math.random() * 0.45 + 0.4,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    let frame = 0;
    const draw = () => {
      frame++;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const distSq = dx * dx + dy * dy;
          const maxDist = 150;
          if (distSq < maxDist * maxDist) {
            const opacity = (1 - Math.sqrt(distSq) / maxDist) * 0.22;
            ctx.strokeStyle = `rgba(56,189,248,${opacity})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }

        const pulse = Math.sin((frame + p.pulse * 60) * 0.025) * 0.18 + 0.82;
        const r = p.radius * pulse;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${p.opacity})`;
        ctx.fill();

        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 4);
        grad.addColorStop(0, `${p.color}${p.opacity * 0.4})`);
        grad.addColorStop(1, `${p.color}0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(p.x - r * 4, p.y - r * 4, r * 8, r * 8);
      }

      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
      data-testid="footer-bg"
    >
      {/* Soft top fade so it bleeds out of the section above */}
      <div className="absolute inset-x-0 top-0 h-24 bg-slate-950 bg-gradient-to-b from-slate-950 to-transparent" />

      {/* Mesh wash — cooler / deeper than hero */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute -top-20 left-1/4 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-teal-500/15 via-cyan-500/8 to-transparent blur-3xl animate-mesh-2" />
        <div className="absolute -bottom-32 -right-16 w-[480px] h-[480px] rounded-full bg-gradient-to-br from-indigo-500/15 via-emerald-500/8 to-transparent blur-3xl animate-mesh-1" />
        <div className="absolute top-10 -left-20 w-[360px] h-[360px] rounded-full bg-gradient-to-br from-emerald-500/10 to-transparent blur-3xl animate-mesh-3" />
      </div>

      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full opacity-70 mix-blend-screen"
      />
    </div>
  );
};

export default FooterBackground;
